import CancelablePromise from 'p-cancelable';
import ajax from '../../../utils/ajax';
import InvalidFlexSessionResponseHandler from './InvalidFlexSessionResponseHandler';

function checkResponseForUnknownError(response) {
  // Reject on 'UnknownError' so we can treat it as a failed request
  return new Promise((resolve, reject) => {
    const { status } = response;

    if (status === 'UnknownError') {
      reject(status);
    } else {
      resolve(response);
    }
  });
}

/**
 * @typedef {Object} ReservePerformanceResponseObject
 * @property {boolean} isReservationSuccessful
 * @property {boolean} isSpecialRequestFailure
 * @property {number} performanceLineItemId
 * @property {boolean} isValidPackage
 * @property {string} groupStatusText
 * @property {string} miniCartHtml
 * @property {string} reservationHtml
 * @property {string} validationMessage
 * @property {number} millisecondsRemaining
 * @property {string} packageSubtotal
 * @property {boolean} hasLockedSeats
 */

/**
 * @typedef {Object} RemovePerformanceResponseObject
 * @property {number} performanceId
 * @property {number} performanceGroupId
 * @property {boolean} isValidPackage
 * @property {string} redirectLocation
 * @property {string} groupStatusText
 * @property {string} miniCartHtml
 * @property {string} packageSubtotal
 */

/**
 * @typedef {Object} GetTicketSelectorResponse
 * @property {string} html
 * @property {boolean} isErrorMessage
 * @property {string} selectedZoneId
 */

/**
 * @typedef {Object} GetSeatSelectionSetup
 * @property {string} ticketSelectionMode 'SYOS' | 'TicketSelector'
 * @property {string} modalTitle
 */

/**
 * @typedef {Object} FlexPackageReservationResponse
 * @property {string} type
 * @property {boolean} isValid
 * @property {boolean} isFormValidationError
 * @property {any} validationResultList
 * @property {ReservePerformanceResponseObject} reservationData
 */

class FlexSessionService {
  /**
   * @param {string} token
   * @param {number} packageId
   * @param {string} detailsPagePath
   */
  constructor(token, packageId, detailsPagePath) {
    this.token = token;
    this.packageId = packageId;
    this.detailsPagePath = detailsPagePath;
  }

  getDetailsPagePath() {
    return this.detailsPagePath;
  }

  getClearSessionPath() {
    return `/packages/flex/session/${this.token}/delete?packageId=${this.packageId}`;
  }

  setSeatingOptions(options) {
    const body = { ...options, isRemovePackageConfirmed: false };

    const url = `/packages/flex/session/${this.token}`;

    return this.post(url, body);
  }

  setSeatingOptionsRemovePackageConfirmed(options) {
    const body = { ...options, isRemovePackageConfirmed: true };

    const url = `/packages/flex/session/${this.token}`;

    return this.post(url, body);
  }

  /**
   * @return {Promise<GetTicketSelectorResponse>}
   */
  getTicketSelector(performanceId) {
    const url = `/packages/flex/selections/${this.token}/ticketselector`;

    const body = { performanceId };

    return this.get(url, body);
  }

  /**
   * @return {Promise<GetSeatSelectionSetup>}
   */
  getSeatSelectionSetup(performanceId, forceBestAvailable = false) {
    const url = `/packages/flex/selections/${this.token}/seatselectionsetup`;

    const body = { performanceId, forceBestAvailable };

    return this.get(url, body);
  }

  validateFinish() {
    const url = `/packages/flex/selections/${this.token}/validatefinish`;

    return this.get(url);
  }

  /**
   * @return {Promise<ReservePerformanceResponseObject>}
   * @deprecated Use `reservePerformanceWithCustomForm` instead
   */
  async reservePerformance(performanceId, performanceGroupId, zoneId) {
    const res = await this.reservePerformanceWithCustomForm({
      performanceId, performanceGroupId, zoneId, customForm: {},
    });

    return res.reservationData;
  }

  /**
   * @return {Promise<FlexPackageReservationResponse>}
   */
  reservePerformanceWithCustomForm(formData) {
    const url = `/packages/flex/selections/${this.token}/reserve`;

    return this.post(url, formData);
  }

  /**
 * @return {Promise<RemovePerformanceResponseObject>}
 */
  removePerformance(performanceLineItemId, performanceId, performanceGroupId) {
    const url = `/packages/flex/selections/${this.token}/delete`;

    return this.post(url, { performanceLineItemId, performanceId, performanceGroupId });
  }

  updateSeatingRequestNotes(value) {
    const url = `/api/session/packagenotes/${this.packageId}/${this.token}`;

    return this.post(url, { value });
  }

  get(url, data) {
    return this.request(ajax.get, url, data);
  }

  post(url, data) {
    return this.request(ajax.post, url, data);
  }

  request(ajaxMethod, url, data = {}) {
    const promise = new CancelablePromise((onCancel, resolve) => {
      const request = ajaxMethod(url, this.getPackageRequestData(data));

      onCancel(() => {
        request.cancel();
      });

      request
        .then(checkResponseForUnknownError)
        .then(InvalidFlexSessionResponseHandler.checkForInvalidFlexSessionResponse)
        .then(resolve)
        .catch(() => {
          if (promise.canceled) {
            return;
          }

          window.location.href = '/error';
        });
    });

    return promise;
  }

  getPackageRequestData(data) {
    const { token, packageId } = this;

    return { ...data, token, packageId };
  }
}

export default FlexSessionService;
